/**
 * FormUndoButtons — the undo and redo buttons over `formUndoStack` (SYD-2447),
 * mounted by `FormControl`, by `RecordDetailPanel`, and — in full screen only — by
 * `ArTableControls`.
 *
 * The repeats are not duplicates. A full-screen ArTable covers the viewport with
 * `position: fixed`, so `FormControl` is off screen; the Records page reaches an
 * ArTable through `RecordDetailPanel`, never through `FormControl`. The row delete
 * no longer asks for confirmation — these buttons are what tells the operator a
 * delete is reversible, so they must be reachable wherever a delete is.
 *
 * Several mounts can be in the DOM at once, so `surface` names the bar and closes
 * every accessible name. One table holds the viewport at a time, so
 * `'table controls'` names one pair however many tables a form renders. Each
 * direction is its own component reading its own leaf of
 * `formUndoStack.availability`, so one direction moving leaves the other unrendered.
 */

import { FC } from 'react';
import { Button, Tooltip } from 'antd';
import { useSnapshot } from 'valtio';

import Icon from '@/components/atoms/Icon';
import { ariaKeyshortcutsFor } from '@/keyboard';
import { formUndoStack, type UndoDirection } from '@/store/form/formUndoStack';
import { useFormUndoDispatch } from '@/use/form/useFormUndoDispatch';

/**
 * Per-bar presentation, matching the sibling controls the pair mounts beside.
 *
 * `trailingGap` separates the pair from the control after it on the form bar, whose
 * middle-size buttons sit close enough to read as one group without it. The two small
 * bars already space their children with a `gap`, so a margin there would show up as a
 * wider space after redo than between any other two controls.
 */
const SURFACES = {
  'form controls': {
    buttonSize: 'middle',
    iconSize: 20,
    iconShift: 5,
    trailingGap: 10,
  },
  'record controls': {
    buttonSize: 'small',
    iconSize: 16,
    iconShift: 2,
    trailingGap: 0,
  },
  'table controls': {
    buttonSize: 'small',
    iconSize: 16,
    iconShift: 2,
    trailingGap: 0,
  },
} as const;

type FormUndoSurface = keyof typeof SURFACES;

type UndoDirectionButtonProps = {
  direction: UndoDirection;
  surface: FormUndoSurface;
  style?: React.CSSProperties;
};

const UndoDirectionButton: FC<UndoDirectionButtonProps> = ({
  direction,
  surface,
  style,
}) => {
  const snap = useSnapshot(formUndoStack.availability[direction]);
  const dispatch = useFormUndoDispatch();
  const { buttonSize, iconSize, iconShift } = SURFACES[surface];

  const action = direction === 'undo' ? 'Undo' : 'Redo';
  const title = snap.label === undefined ? action : `${action} ${snap.label}`;
  const Glyph = direction === 'undo' ? Icon.RotateCcw : Icon.RotateCw;

  return (
    <Tooltip title={title}>
      <Button
        type="text"
        size={buttonSize}
        disabled={!snap.available}
        aria-label={`${title}, ${surface}`}
        aria-keyshortcuts={ariaKeyshortcutsFor(`form.${direction}`)}
        icon={<Glyph size={iconSize} shift={iconShift} />}
        onClick={() => dispatch(direction)}
        style={style}
      />
    </Tooltip>
  );
};

const FormUndoButtons: FC<{ surface: FormUndoSurface }> = ({ surface }) => (
  <>
    <UndoDirectionButton direction="undo" surface={surface} />
    <UndoDirectionButton
      direction="redo"
      surface={surface}
      style={{ marginRight: SURFACES[surface].trailingGap }}
    />
  </>
);

export default FormUndoButtons;
